import React, { useEffect, useState } from "react";
import { View } from "react-native";
import Hero from "../../Hero/Hero";
import { CarouselItem } from "../../../types/page";
import { APIResponse } from "../../../types/responses/APIResponse";
import { fetchGet } from "../../../client";
import { RectangleLoader } from "../../States/Loading/Loaders";
import { useResponsive } from "../../../hooks/useResponsive";

/**
 * HeroAttachment is a reusable CMS attachment (RAttachment) that fetches
 * and displays a Hero carousel similar to the homepage hero
 *
 * This component handles its own data fetching, loading and error states
 *
 * @param id - The ID of the Hero to fetch and display
 *
 * @example <HeroAttachment id="1" />
 *
 * @return A Hero component displaying the fetched Hero data
 *
 */

type Props = {
  id: string;
};

const HeroAttachment = ({ id }: Props) => {
  const [data, setData] = useState<CarouselItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { isSmallAndDown } = useResponsive();

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const res: APIResponse<{ hero: { carousel_items: CarouselItem[] } }> =
          await fetchGet(`/heroes/${id}`);

        if (res?.hero && res?.hero?.carousel_items) {
          setData(res.hero.carousel_items || []);
        } else setError("No data found");
      } catch (err) {
        setError(
          "Error loading race: " +
            (err instanceof Error ? err.message : "Unknown")
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading)
    return (
      // This aspect ratio is slightly adjusted to roughly match the Hero's height
      // padding and the diff columns/spacing make it hard to match exactly
      // This prevents too much layout shift
      <View
        style={{ width: "100%", aspectRatio: 16 / (isSmallAndDown ? 8 : 6.5) }}
      >
        <RectangleLoader height="100%" width="100%" />
      </View>
    );

  if (error) return null;

  if (!data?.length) return null;

  return (
    <View
      style={{
        width: "100%",
      }}
    >
      <Hero heroData={data} isAttachment />
    </View>
  );
};

export default HeroAttachment;
